<template>
  <div v-if="customer" class="py-6 lg:flex w-full">
    <div class="w-full lg:w-2/3">
      <div class="flex justify-between items-center mb-4 border-b pb-4">
        <div class="flex items-center">
          <h4 class="text-lg mr-4 leading-none font-medium opacity-75">
            {{ $t('general.alerts') }}
          </h4>
          <o-chip class="mb-0 flex items-center space-x-2">
            {{ alerts.length }}
          </o-chip>
        </div>

        <div class="flex items-center space-x-2 flex-shrink-0">
          <o-button variant="primary" flat @click="handleAddNote(customerNoteTypes.alert)">
            {{ $t('components.customer.profile.notes.addAlert') }}
          </o-button>
        </div>
      </div>
      <div v-if="alerts.length" class="flex flex-col divide-y overflow-hidden p-2 md:p-2 items-center bg-red-500 dark:bg-red-950 dark:border dark:border-red-900 shadow-lg rounded-2xl mb-10 divide-red-400 dark:divide-red-900">
        <div v-for="alert in alerts" :key="alert.idNote" class="flex items-center justify-between w-full p-4">
          <div class="flex items-center space-x-5">
            <o-icon icon="danger" :size="20" class="text-white" />
            <div>
              <p class="text-xs text-white/75 mb-2">
                {{ $date.format(alert.created * 1000, $date.presets.full) }}
              </p>

              <p class="text-sm text-white">
                <span class="font-semibold">{{ $t(AlertTypeLabels[alert.type]) }}</span>
                <span class="whitespace-pre-line" v-html="alert.content" />
              </p>
            </div>
          </div>

          <div class="flex items-center space-x-1 justify-end">
            <o-button transparent variant="light" icon="edit" size="sm" @click="handleEditNote(alert)" />
            <o-button transparent variant="light" icon="delete" size="sm" @click="handleDeleteNote(alert)" />
          </div>
        </div>
      </div>
      <div v-if="!alerts.length" class="mb-10">
        <p class="text-sm opacity-60">
          {{ $t('components.customer.profile.notes.noAlerts') }}
        </p>
      </div>
      <o-pager
        :header="$t('general.notes')"
        :button="$t('general.loadMore')"
        :empty="$t('components.customer.profile.notes.noNotes')"
        header-class="flex justify-between items-center mb-4 border-b pb-4"
        :list="notes"
      >
        <template #action>
          <div class="flex items-center space-x-2 flex-shrink-0">
            <segment-field
              v-model="typeFilter"
              :options="typeFilterOptions"
              class="border dark:border-gray-800"
            />
            <o-button variant="primary" flat @click="handleAddNote()">
              {{ $t('note.add') }}
            </o-button>
          </div>
        </template>
        <template #default="{data}">
          <div v-for="(group, key) in split(data)" :key="key" class="mb-8">
            <p class="text-xs mb-3 tracking-wider opacity-60 uppercase">
              {{ key }}
            </p>
            <div class="flex flex-col divide-y overflow-hidden p-2 md:p-2 items-center h-full bg-white dark:bg-gray-800/20 dark:border dark:border-gray-800 shadow-lg rounded-2xl mb-4">
              <div v-for="note in group" :key="note.idNote" class="w-full p-4 flex items-center justify-between">
                <div class="flex items-center space-x-5">
                  <o-icon :icon="note.isTypeReservation ? 'reservation' : 'notes'" :size="20" class="opacity-60" />
                  <div>
                    <p class="text-xs opacity-60 mb-2">
                      {{ note.dateLabel }}
                    </p>

                    <p class="text-sm">
                      <span class="font-semibold">{{ $t(CombinedNoteTypeLabels[note.type]) }}</span>
                      <span class="whitespace-pre-line" v-html="note.content" />
                    </p>
                  </div>
                </div>

                <div class="flex items-center space-x-1 justify-end">
                  <template v-if="note.isTypeCustomerNote">
                    <o-history-link
                      :item="note.customerNote"
                      model="CustomerNote"
                      component="OButton"
                      slim
                      flat
                      size="sm"
                      icon="history"
                    />

                    <o-button flat icon="edit" size="sm" @click="handleEditNote(note)" />
                    <o-button flat variant="danger" icon="delete" size="sm" @click="handleDeleteNote(note)" />
                  </template>

                  <template v-if="note.isTypeReservation">
                    <o-panel-link tag="o-button" :path="`/appointment/${note.reservation.idReservation}?date=${note.reservation.date}`" flat icon="reservation" size="sm" />
                  </template>
                </div>
              </div>
            </div>
          </div>
        </template>
      </o-pager>
    </div>

    <div class="w-full flex flex-col max-h-sm lg:w-1/3 bg-gray-100 dark:bg-gray-800/20 dark:border dark:border-gray-800 rounded-xl lg:ml-12 p-4">
      <div class="mb-3 flex items-center justify-between">
        <h4 class="leading-none font-medium opacity-75">
          {{ $t('components.customer.profile.notes.quickNotes') }}
        </h4>

        <o-button icon="save" size="sm" flat @click="handleSave">
          {{ $t('general.save') }}
        </o-button>
      </div>
      <text-area-field v-model="_customer.notes" class="h-full" />
    </div>

    <add-note-dialog
      ref="addNoteDialog"
      :customer="customer"
      @success="handleUpdate"
    />

    <edit-note-dialog
      ref="editNoteDialog"
      :customer="customer"
      @success="handleUpdate"
    />

    <delete-note-dialog
      ref="deleteNoteDialog"
      :customer="customer"
      @success="handleUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import { EndlessListPager } from '@/_api/lib/pager'

import { CustomerNoteType } from '~/_api/models/customerNote'
import { CombinedNoteTypeLabels } from '~/_api/models/customer'
import { AlertTypeLabels } from '~/_api/models/alert'

import AddNoteDialog from '@/components/customerNote/actions/Add.vue'
import EditNoteDialog from '@/components/customerNote/actions/Edit.vue'
import DeleteNoteDialog from '@/components/customerNote/actions/Delete.vue'

import { OPager } from '@/components/elements'
import { SegmentField, TextAreaField } from '@/components/fields'

interface Props {
  customer: Record<string, any>
}

const props = defineProps<Props>()
const emit = defineEmits<{
  (e: 'update:customer', value: any): void
}>()

const { $notification, $date, $api } = useNuxtApp()
const { t } = useI18n()

const addNoteDialog = ref<any>()
const editNoteDialog = ref<any>()
const deleteNoteDialog = ref<any>()
const typeFilter = ref(null)

const notes = reactive(new EndlessListPager((page: number) => $api.customer.combinedNotes(String(props.customer.idCustomer), { page, type: typeFilter.value })))
const customerNoteTypes = CustomerNoteType

const _customer = computed({
  get () {
    return props.customer
  },
  set (value) {
    emit('update:customer', value)
  },
})

const alerts = computed(() => props.customer.alerts)
const typeFilterOptions = computed(() => [
  { label: t('general.all'), value: null },
  { label: t('general.notes'), value: 'note' },
  { label: t('general.appointments'), value: 'reservation' },
])

const handleAddNote = (type = CustomerNoteType.note) => {
  addNoteDialog.value.open(type)
}

const handleEditNote = (customerNote: any) => {
  editNoteDialog.value.open(customerNote.customerNote || customerNote)
}

const handleDeleteNote = (customerNote: any) => {
  deleteNoteDialog.value.open(customerNote.customerNote || customerNote)
}

const handleUpdate = () => {
  notes.reload()
}

watch(typeFilter, () => {
  notes.reload()
})

const handleSave = async () => {
  try {
    const response = await $api.customer.save(props.customer)

    if (response?.data) {
      _customer.value = response.data
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }
}

const split = (data: any[]) => {
  return data.reduce((acc: Record<string, any[]>, cur: any) => {
    const date = ($date as any).format(cur.date * 1000, 'MMMM yyyy')
    acc[date] = [...acc[date] || [], cur]
    return acc
  }, {})
}

onMounted(() => {
  notes.load()
})
</script>
